import { useMemo } from "react";

import { MAMISON_RESORT } from "@features/map";

import type { MapPoints } from "@shared/types/map-types";
import { parseYandexCoords } from "@shared/utils";

import type { MapPageType } from "@/_pages/map/model";

type MapDataSubset = Pick<
  MapPageType,
  "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
>;

interface UsePointMapParams {
  mapData?: MapDataSubset | null;
  pointCoordinates?: string | null;
  focusZoom?: number;
}

interface UsePointMapResult {
  mapPoints: MapPoints;
  focusLocation: {
    center: [number, number];
    zoom: number;
  } | null;
}

/**
 * Хук для подготовки данных карты для страниц объектов (кафе, жилье и т.д.)
 * Преобразует данные из API в формат для отображения на карте с фокусом на конкретной точке
 */
export const usePointMap = (params: UsePointMapParams): UsePointMapResult => {
  const { mapData, pointCoordinates, focusZoom = 16 } = params;

  const mapPoints: MapPoints = useMemo(() => {
    const res: MapPoints = {
      resort: MAMISON_RESORT,
    };

    if (mapData) {
      Object.entries(mapData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          res[key] = value
            // biome-ignore lint/suspicious/noExplicitAny: Динамические данные из API с разными типами точек
            .filter((p: any) => p.coordinates && p.coordinates !== "null")
            // biome-ignore lint/suspicious/noExplicitAny: Динамические данные из API с разными типами точек
            .map((p: any) => {
              let href = "";
              if (key === "cafes-restaurants")
                href = `/cafes-restaurants/${p.uid}`;
              if (key === "accommodations") href = `/accommodations/${p.uid}`;
              if (key === "cultural-objects")
                href = `/cultural-objects/${p.uid}`;

              return {
                ...p,
                coordinates: String(p.coordinates),
                href: href || undefined,
                target: href ? "_blank" : undefined,
              };
            });
        }
      });
    }

    return res;
  }, [mapData]);

  const focusLocation = useMemo(() => {
    if (!pointCoordinates) return null;

    try {
      const coords = parseYandexCoords(pointCoordinates);
      if (!coords) return null;

      return {
        center: coords as [number, number],
        zoom: focusZoom,
      };
    } catch {
      return null;
    }
  }, [pointCoordinates, focusZoom]);

  return {
    mapPoints,
    focusLocation,
  };
};
